import type { BibleBook, VerseReference } from '@/lib/utils/bible-utils';
import { useRef, useCallback, useEffect, useState } from 'react';

// Debug logging flag - set to true to enable detailed logging
const DEBUG_VERSE_TRACKING = false;

interface VerseData extends VisibleVerse {
    element: Element;
    isIntersecting: boolean;
}

interface VisibleVerse extends VerseReference {
    paragraphIndex: number;
}

/**
 * Custom hook to track the currently visible verse using Intersection Observer.
 * Optimized to minimize re-renders by using refs and a single observer instance.
 * 
 * Tracks verse markers (<sup> elements) as they become visible in the viewport,
 * and identifies the topmost visible verse for scroll position tracking.
 */
export function useVisibleVerse() {
    const [currentVerse, setCurrentVerse] = useState<VisibleVerse | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const visibleVersesRef = useRef<Map<Element, VerseData>>(new Map());
    const updateTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Initialize the Intersection Observer once
    useEffect(() => {
        if (DEBUG_VERSE_TRACKING) {
            console.log('[useVisibleVerse] Initializing Intersection Observer');
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (DEBUG_VERSE_TRACKING) {
                    console.log('[useVisibleVerse] Intersection callback fired with', entries.length, 'entries');
                }

                // Update the map of visible verses
                entries.forEach((entry) => {
                    const verseData = visibleVersesRef.current.get(entry.target);
                    if (!verseData) {
                        // This can happen when a verse unmounts but the Intersection Observer
                        // still has pending callbacks. This is normal and can be safely ignored.
                        if (DEBUG_VERSE_TRACKING) {
                            console.log('[useVisibleVerse] Ignoring callback for unmounted verse element');
                        }
                        return;
                    }

                    if (entry.isIntersecting) {
                        // Verse marker is visible - update the verseData with isIntersecting: true
                        visibleVersesRef.current.set(entry.target, { ...verseData, isIntersecting: true });
                        if (DEBUG_VERSE_TRACKING) {
                            console.log('[useVisibleVerse] Verse ENTERED viewport:',
                                `${verseData.book} ${verseData.chapter}:${verseData.verse}`,
                                `(paragraph ${verseData.paragraphIndex})`,
                                `rect.top=${entry.boundingClientRect.top.toFixed(1)}px`
                            );
                        }
                    } else {
                        // Verse marker is not visible - update the verseData with isIntersecting: false
                        // Keep it in the map so we can track it when it comes back into view
                        visibleVersesRef.current.set(entry.target, { ...verseData, isIntersecting: false });
                        if (DEBUG_VERSE_TRACKING) {
                            console.log('[useVisibleVerse] Verse EXITED viewport:',
                                `${verseData.book} ${verseData.chapter}:${verseData.verse}`,
                                `(paragraph ${verseData.paragraphIndex})`
                            );
                        }
                    }
                });

                if (DEBUG_VERSE_TRACKING) {
                    const visibleVerses = Array.from(visibleVersesRef.current.values())
                        .map(v => `${v.book} ${v.chapter}:${v.verse}`)
                        .join(', ');
                    console.log('[useVisibleVerse] Currently visible verses:',
                        visibleVersesRef.current.size,
                        visibleVerses ? `[${visibleVerses}]` : '(none)'
                    );
                }

                // Debounce the verse update to avoid excessive state changes
                if (updateTimeoutRef.current) {
                    clearTimeout(updateTimeoutRef.current);
                }

                updateTimeoutRef.current = setTimeout(() => {
                    if (DEBUG_VERSE_TRACKING) {
                        console.log('[useVisibleVerse] Debounce timeout fired, finding topmost verse...');
                    }

                    // Find the topmost visible verse marker
                    let topMostVerse: VerseData | null = null;
                    let topMostY = Infinity;

                    const candidates: Array<{verse: VerseData, y: number, intersecting: boolean}> = [];

                    visibleVersesRef.current.forEach((verseData) => {
                        // Only consider verses that are actually intersecting with the viewport
                        if (!verseData.isIntersecting) {
                            return;
                        }

                        const rect = verseData.element.getBoundingClientRect();
                        // Consider verses in the top portion of the viewport
                        // Account for header (48px) and some buffer
                        const headerHeight = 48;

                        if (DEBUG_VERSE_TRACKING) {
                            candidates.push({
                                verse: verseData,
                                y: rect.top,
                                intersecting: verseData.isIntersecting
                            });
                        }

                        if (rect.top >= headerHeight && rect.top < topMostY) {
                            topMostY = rect.top;
                            topMostVerse = verseData;
                        }
                    });

                    if (DEBUG_VERSE_TRACKING) {
                        console.log('[useVisibleVerse] Verse candidates (sorted by Y position):');
                        candidates
                            .sort((a, b) => a.y - b.y)
                            .forEach(({verse, y, intersecting}) => {
                                const isSelected = verse === topMostVerse;
                                console.log(
                                    `  ${isSelected ? '→' : ' '} ${verse.book} ${verse.chapter}:${verse.verse}`,
                                    `(para ${verse.paragraphIndex})`,
                                    `y=${y.toFixed(1)}px`,
                                    y < 48 ? '(above header)' : '',
                                    intersecting ? '✓' : '✗'
                                );
                            });

                        if (topMostVerse) {
                            console.log('[useVisibleVerse] Selected topmost verse:',
                                `${topMostVerse.book} ${topMostVerse.chapter}:${topMostVerse.verse}`,
                                `(paragraph ${topMostVerse.paragraphIndex})`,
                                `at y=${topMostY.toFixed(1)}px`
                            );
                        } else {
                            console.warn('[useVisibleVerse] No topmost verse found! All verses may be above header.');
                        }
                    }

                    // Only update state if the verse has changed
                    if (topMostVerse) {
                        setCurrentVerse((prev) => {
                            // Avoid unnecessary updates if verse hasn't changed
                            if (
                                prev?.book === topMostVerse.book &&
                                prev?.chapter === topMostVerse.chapter &&
                                prev?.verse === topMostVerse.verse
                            ) {
                                if (DEBUG_VERSE_TRACKING) {
                                    console.log('[useVisibleVerse] Verse unchanged, skipping state update');
                                }
                                return prev;
                            }
                            if (DEBUG_VERSE_TRACKING) {
                                console.log('[useVisibleVerse] ✅ UPDATING currentVerse:',
                                    `${topMostVerse.book} ${topMostVerse.chapter}:${topMostVerse.verse}`
                                );
                            }
                            return {
                                book: topMostVerse.book,
                                chapter: topMostVerse.chapter,
                                verse: topMostVerse.verse,
                                paragraphIndex: topMostVerse.paragraphIndex,
                            };
                        });
                    }
                }, 300); // 300ms debounce for responsive header updates
            },
            {
                // Track visibility with multiple thresholds for accuracy
                threshold: [0, 0.5, 1.0],
                // Don't use rootMargin - we'll filter by header position in the callback instead
                // This ensures verses just above the header are still tracked
                rootMargin: '0px 0px 0px 0px',
            }
        );

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, []);

    // Stable callback to register a verse marker for observation
    const registerVerse = useCallback((
        element: Element | null,
        book: BibleBook,
        chapter: number,
        verse: number,
        paragraphIndex: number
    ) => {
        if (!element || !observerRef.current) {
            if (DEBUG_VERSE_TRACKING && !element) {
                console.warn('[useVisibleVerse] registerVerse called with null element');
            }
            return;
        }

        // Store verse data
        const verseData: VerseData = {
            element,
            book,
            chapter,
            verse,
            paragraphIndex,
            isIntersecting: false, // Will be updated by IntersectionObserver callback
        };
        visibleVersesRef.current.set(element, verseData);

        if (DEBUG_VERSE_TRACKING) {
            const rect = element.getBoundingClientRect();
            console.log('[useVisibleVerse] Registered verse:',
                `${book} ${chapter}:${verse}`,
                `(paragraph ${paragraphIndex})`,
                `initial position: y=${rect.top.toFixed(1)}px`,
                `visible: ${rect.top >= 0 && rect.top < window.innerHeight}`
            );
        }

        // Start observing
        observerRef.current.observe(element);

        // Return cleanup function
        return () => {
            // Remove from map first to prevent race conditions with pending callbacks
            visibleVersesRef.current.delete(element);

            // Then unobserve
            if (observerRef.current) {
                observerRef.current.unobserve(element);
            }

            if (DEBUG_VERSE_TRACKING) {
                console.log('[useVisibleVerse] Unregistered verse:',
                    `${book} ${chapter}:${verse}`,
                    `(paragraph ${paragraphIndex})`
                );
            }
        };
    }, []);

    return {
        currentVerse,
        registerVerse
    };
}
